import type { MinutesJobStatus, TranscriptSegment } from '@ai-meeting/shared'
import { ApiError } from '@/services/api'
import { getServiceStrings } from '@/services/i18n'
import { updateMeeting } from '@/services/meetings'
import { pollMinutesJobUntilSettled } from '@/utils/pollMinutesJobUntilSettled'
import { useStore } from '@/store/useStore'

const activeInternalMinutesJobs = new Set<string>()

function transcriptFromJob(job: MinutesJobStatus): TranscriptSegment[] | undefined {
  const text = job.transcript?.trim()
  if (!text) return undefined
  return [{ speaker: 'Meeting', text, timestamp: 0 }]
}

export async function persistInternalMinutesJobResult(
  meetingId: string,
  job: MinutesJobStatus
): Promise<void> {
  if (job.status !== 'succeeded') return

  await updateMeeting(meetingId, {
    insights: job.meetingMinutes ?? null,
    transcript: transcriptFromJob(job),
  })
}

/**
 * After End Meeting (flow C): poll the BRAI-460 job in the background, persist
 * minutes/transcript when ready, then clear pending state and toast outcome.
 */
export function runInternalMinutesJobInBackground(meetingId: string, jobId: string): void {
  const dedupeKey = `${meetingId}:${jobId}`
  if (activeInternalMinutesJobs.has(dedupeKey)) return
  activeInternalMinutesJobs.add(dedupeKey)

  void (async () => {
    const { removePendingSavedMeeting, showSaveToast, clearInternalMinutesJobId } =
      useStore.getState()
    const t = getServiceStrings()

    const clearPendingState = () => {
      removePendingSavedMeeting(meetingId)
      clearInternalMinutesJobId(meetingId)
    }

    try {
      const job = await pollMinutesJobUntilSettled(jobId)
      if (job.status === 'failed') {
        showSaveToast({ kind: 'error', message: t.internalMeetingFailed })
        clearPendingState()
        return
      }
      await persistInternalMinutesJobResult(meetingId, job)
      showSaveToast({ kind: 'success', message: t.meetingSavedToast })
      clearPendingState()
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        showSaveToast({ kind: 'error', message: t.internalMeetingExpired })
        clearPendingState()
        return
      }
      showSaveToast({ kind: 'error', message: t.internalMeetingFailed })
      // Keep job id + pending row so a reload or detail poll can retry persist.
    } finally {
      activeInternalMinutesJobs.delete(dedupeKey)
    }
  })()
}
